import { useState } from 'react';
import {
  Box,
  Button as MantineButton,
  Center,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconX } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router';
import { Button } from 'src/design-system/Button';
import { IconButton } from 'src/design-system/IconButton';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { MeetingPlatformIcon } from 'src/meetings/components/MeetingPlatformIcon';
import { useBotStatus } from 'src/meetings/hooks/useBotStatus';
import { useForceBotJoin } from 'src/meetings/hooks/useForceBotJoin';
import { type FamilyMeeting, useFamilyMeetings } from 'src/meetings/hooks/useFamilyMeetings';
import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import { TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import { formatMeetingDuration, isPastEvent } from 'src/meetings/utils/date-utils';
import {
  getAllClientParticipants,
  getParticipantDisplayNames,
} from 'src/meetings/utils/event-utils';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { AddExpectedAttendeesModal } from './AddExpectedAttendeesModal';
import { GenerateMeetingNoteModal } from './GenerateMeetingNoteModal';
import { MeetingAgendaCard } from './MeetingAgendaCard';
import { MeetingDetailsCard } from './MeetingDetailsCard';
import { ParticipantInfoCard } from './ParticipantInfoCard';
import { ParticipantPill } from './ParticipantPill';
import { PastMeetingTabs } from './PastMeetingTabs';
import { SelectAgendaMembersModal } from './SelectAgendaMembersModal';
import { SuggestedActions } from './SuggestedActions';

export default function MeetingDetailsScreen() {
  const { familyId = '', eventId = '' } = useParams<{ familyId: string; eventId: string }>();
  const navigate = useNavigate();
  const { events, isLoading } = useFamilyMeetings(familyId);
  const { family } = useFamily(familyId);
  const { clients } = useClients({ familyId }, { enabled: !!familyId });
  const { forceBotJoin, isLoading: isBotJoinLoading } = useForceBotJoin(eventId);
  const { botStatus, isLoading: isBotStatusLoading } = useBotStatus(eventId);
  const [attendeesOpen, setAttendeesOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [agendaMembersOpen, setAgendaMembersOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);

  const meeting = events.find(event => event.id === eventId) ?? null;
  const meetingsPath = `/family/${familyId}/meetings`;

  // A finished meeting with a ready transcript can have a meeting note generated.
  const isPast = meeting ? isPastEvent(meeting) : false;
  const { transcript } = useTranscript({
    eventId,
    enabled: isPast && !!meeting?.hasTranscription,
  });
  const transcriptReady = transcript?.status === TranscriptionStatus.READY;
  const { meetingNote } = useCalendarEventMeetingNote(eventId, {
    enabled: isPast && transcriptReady,
  });
  const noteExistsOrGenerating =
    meetingNote?.status === MeetingNoteStatus.READY ||
    meetingNote?.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING;
  // Only offer to generate a note when the transcript is ready and there isn't one already.
  const canCreateNote = isPast && transcriptReady && !noteExistsOrGenerating;

  if (isLoading && !meeting) {
    return (
      <Center py="xxl">
        <Loader />
      </Center>
    );
  }

  if (!meeting) {
    return <Text c="gray.6">This meeting could not be found.</Text>;
  }

  const meetingUrl = meeting.meetingUrl;
  const clientById = Object.fromEntries(clients.map(client => [client.id, client]));

  const clientParticipants = getAllClientParticipants(meeting)
    .map(p => p.participant.client)
    .filter((client): client is NonNullable<typeof client> => client !== null);

  const participantNames = getParticipantDisplayNames(meeting);

  const participantInfo = clientParticipants.map(simple => {
    const full = clientById[simple.id];
    return {
      id: simple.id,
      name: `${simple.firstName} ${simple.lastName}`.trim(),
      address: full?.personalDetails?.address ?? null,
      email: full?.emailAddress ?? simple.emailAddress ?? null,
      phone: full?.personalDetails?.phoneNumber ?? null,
    };
  });

  const mainClient = family?.mainFamilyMember.clientId
    ? clientById[family.mainFamilyMember.clientId]
    : undefined;
  const familyName = family?.name ?? 'Family';
  const mainMemberName = mainClient
    ? formatName(mainClient.firstName, mainClient.lastName, NameFormat.NATURAL)
    : (participantInfo[0]?.name ?? 'this client');

  const openMeeting = () => {
    if (meetingUrl) window.open(meetingUrl, '_blank');
  };

  const handleForceBotJoin = async () => {
    try {
      await forceBotJoin();
      notifications.show({
        title: 'Bot joining meeting',
        message: 'The meeting bot will join within 10 seconds',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Failed to schedule bot',
        message: 'Could not reschedule the bot to join now. Please try again.',
        color: 'red',
      });
    }
  };

  const handleJoinMeetingClick = () => {
    const minutesFromStart =
      (Date.now() - new Date(meeting.scheduledStartAt).getTime()) / 1000 / 60;

    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CLICKED, {
      botStatus: botStatus || 'UNKNOWN',
      minutesFromStart: Math.abs(minutesFromStart),
      meetingUrl,
    });

    if (
      botStatus === 'ACTIVE' ||
      botStatus === 'NONE' ||
      botStatus === 'FINISHED' ||
      Math.abs(minutesFromStart) <= 2
    ) {
      openMeeting();
      return;
    }

    setRescheduleOpen(true);
  };

  const handleConfirmJoin = async (rescheduleBot: boolean) => {
    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CONFIRMED, { rescheduleBot, meetingUrl });
    if (rescheduleBot) await handleForceBotJoin();
    setRescheduleOpen(false);
    openMeeting();
  };

  const handleAddBotClick = async () => {
    Mixpanel.track(MixpanelEvents.BOT_MANUALLY_ADDED_TO_MEETING);
    await handleForceBotJoin();
  };

  const dateTimePill = (
    <Flex align="center" p="xxs" bg="gray.1" style={{ borderRadius: 12, width: 'fit-content' }}>
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.7">
        {formatSchedule(meeting)}
      </Text>
    </Flex>
  );

  return (
    <>
      <Box pt={36} px="xxl" pb="xxl">
        {isPast ? (
          <Flex align="center" gap="xxs">
            <IconButton
              size={36}
              bg="gray.1"
              c="gray.9"
              aria-label="Back to meetings"
              onClick={() => navigate(meetingsPath)}
              style={{ borderRadius: 12 }}
            >
              <IconArrowLeft size={20} />
            </IconButton>
            {dateTimePill}
            {participantNames.length > 0 && (
              <ParticipantPill
                participantNames={participantNames}
                onOpen={() => setAttendeesOpen(true)}
                radius={12}
                textSize="textSm"
                nameColor="gray.10"
                iconSize={20}
                px="xs"
                py="xxs"
                gap={8}
              />
            )}
          </Flex>
        ) : (
          <Flex justify="space-between" align="flex-start" gap="sm">
            {dateTimePill}
            <IconButton
              size={40}
              bg="gray.1"
              c="gray.9"
              aria-label="Close"
              onClick={() => navigate(meetingsPath)}
            >
              <IconX size={20} />
            </IconButton>
          </Flex>
        )}

        <Flex align="center" mt="xs" py={5}>
          {meeting.meetingPlatform && (
            <Box style={{ paddingRight: 12 }}>
              <MeetingPlatformIcon platform={meeting.meetingPlatform} size={28} />
            </Box>
          )}
          <Text size="displaySm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {meeting.title}
          </Text>
        </Flex>

        {isPast ? (
          <>
            {canCreateNote && (
              <Box mt="sm">
                <SuggestedActions
                  participantNames={participantNames}
                  onEdit={() => setNoteModalOpen(true)}
                  onGenerate={() => setNoteModalOpen(true)}
                />
              </Box>
            )}
            <Box mt="lg">
              <PastMeetingTabs meeting={meeting} transcript={transcript} />
            </Box>
          </>
        ) : (
          <>
            <Flex justify="space-between" align="center" mt="sm" gap="sm">
              {participantNames.length > 0 ? (
                <ParticipantPill
                  participantNames={participantNames}
                  onOpen={() => setAttendeesOpen(true)}
                  radius={12}
                  textSize="textSm"
                  nameColor="gray.10"
                  iconSize={20}
                />
              ) : (
                <Box />
              )}
              <Group gap="sm" wrap="nowrap">
                <Button
                  variant="primary"
                  size="sm"
                  w={160}
                  h={36}
                  style={{ borderRadius: 12 }}
                  disabled={!meetingUrl || isBotJoinLoading}
                  onClick={handleJoinMeetingClick}
                >
                  Join meeting
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  w={160}
                  h={36}
                  style={{ borderRadius: 12 }}
                  loading={isBotJoinLoading || isBotStatusLoading}
                  disabled={botStatus === 'ACTIVE' || !meetingUrl}
                  onClick={handleAddBotClick}
                >
                  {botStatus === 'ACTIVE' ? 'Bot recording' : 'Add bot'}
                </Button>
              </Group>
            </Flex>

            <Divider mt="md" color="gray.1" />

            <Text mt="lg" size="xl" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Meeting preparation
            </Text>

            <Stack mt="sm" gap="sm">
              <MeetingDetailsCard title={`${familyName} overview`} />

              {participantInfo.map(participant => (
                <ParticipantInfoCard
                  key={participant.id}
                  name={participant.name}
                  address={participant.address}
                  email={participant.email}
                  phone={participant.phone}
                />
              ))}

              <MeetingAgendaCard
                eventId={meeting.id}
                hasMeetingAgenda={meeting.hasMeetingAgenda}
                mainMemberName={mainMemberName}
                familyName={familyName}
                onEditMembers={() => setAgendaMembersOpen(true)}
              />
            </Stack>
          </>
        )}
      </Box>

      <AddExpectedAttendeesModal
        opened={attendeesOpen}
        onClose={() => setAttendeesOpen(false)}
        familyId={familyId}
        meeting={meeting}
      />

      <SelectAgendaMembersModal
        opened={agendaMembersOpen}
        onClose={() => setAgendaMembersOpen(false)}
        familyId={familyId}
      />

      <GenerateMeetingNoteModal
        opened={noteModalOpen}
        onClose={() => setNoteModalOpen(false)}
        familyId={familyId}
        meeting={meeting}
      />

      <Modal
        opened={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
        title="Reschedule meeting bot?"
        centered
      >
        <Text size="textMd" mb="xs">
          Do you want to reschedule the bot to join the meeting immediately?
        </Text>
        <Text size="textSm" c="gray.6" mb="lg">
          If the meeting is starting early or you want the bot to join now, select &quot;Yes&quot;.
          Otherwise, the bot will join at the scheduled time.
        </Text>
        <Group justify="flex-end" gap="sm">
          <MantineButton
            variant="subtle"
            onClick={() => handleConfirmJoin(false)}
            disabled={isBotJoinLoading}
          >
            No, just join
          </MantineButton>
          <MantineButton
            variant="filled"
            onClick={() => handleConfirmJoin(true)}
            loading={isBotJoinLoading}
          >
            Yes, reschedule bot
          </MantineButton>
        </Group>
      </Modal>
    </>
  );
}

function formatSchedule(meeting: FamilyMeeting): string {
  const startIso = meeting.startAt ?? meeting.scheduledStartAt;
  const endIso = meeting.endAt ?? meeting.scheduledEndAt;
  const start = new Date(startIso);
  const end = new Date(endIso);

  const date = start.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });

  const startMeridiem = start.getHours() >= 12 ? 'PM' : 'AM';
  const endMeridiem = end.getHours() >= 12 ? 'PM' : 'AM';
  const startText = formatClock(start, startMeridiem !== endMeridiem);
  const endText = formatClock(end, true);

  return `${date}, ${startText}-${endText} (${formatMeetingDuration(startIso, endIso)})`;
}

function formatClock(date: Date, withMeridiem: boolean): string {
  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const meridiem = date.getHours() >= 12 ? 'PM' : 'AM';
  return withMeridiem ? `${hours}:${minutes}${meridiem}` : `${hours}:${minutes}`;
}
